
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import tinycolor from "tinycolor2";
import { BRAND } from "./brand/brand";
import { userAPI } from "./UserAPI";
import { useAtom } from "jotai";
import { themeAtom, authenticationAtom, themeLoadingAtom } from './State';

import { ThemeProvider } from "@/components/fluent-styles-compat";
import { normalizeTheme, shadcnTokensFor } from "./themeTokens";
/*
primary-color: #001A61
$primary-color-dark: #002077
$primary-color-darkest: #00268E
$primary-color-light: #2F65F9
$primary-color-lightest: #ADC3FF


*/

// export const styleCalculator = [
//     { direction: -1, factor: 10, name: 'Primary Darker', color: '#00268E', field: 'primaryDarker' },
//     { direction: -1, factor: 5, name: 'Primary Dark', color: '#00268E', field: 'primaryDark' },
//     { direction: 1, factor: 0, name: 'Primary', color: '#00268E', field: 'primary' },
//     { direction: 1, factor: 30, name: 'Primary Light', color: '#00268E', field: 'primaryLight' },
//     { direction: 1, factor: 70, name: 'Primary Lighter', color: '#00268E', field: 'primaryLighter' },
//     { direction: -1, factor: 10, name: 'Secondary Darker', color: '#5F3A79', field: 'secondaryDarker' },
//     { direction: -1, factor: 5, name: 'Secondary Dark', color: '#5F3A79', field: 'secondaryDark' },
//     { direction: 1, factor: 0, name: 'Secondary', color: '#5F3A79', field: 'secondary' },
//     { direction: 1, factor: 40, name: 'Secondary Light', color: '#5F3A79', field: 'secondaryLight' },
//     { direction: 1, factor: 45, name: 'Secondary Lighter', color: '#5F3A79', field: 'secondaryLighter' },
//     { direction: -1, factor: 10, name: 'Tertiary Darker', color: '#98AC4D', field: 'tertiaryDarker' },
//     { direction: -1, factor: 5, name: 'Tertiary Dark', color: '#98AC4D', field: 'tertiaryDark' },
//     { direction: 1, factor: 0, name: 'Tertiary', color: '#98AC4D', field: 'tertiary' },
//     { direction: 1, factor: 40, name: 'Tertiary Light', color: '#98AC4D', field: 'tertiaryLight' },
//     { direction: 1, factor: 45, name: 'Tertiary Lighter', color: '#98AC4D', field: 'tertiaryLighter' },
//     { direction: 1, factor: 0, name: 'Accent', color: '#1E1A76', field: 'accent' },
// ];

export const styleCalculator = [
    { direction: -1, factor: 10, name: 'Primary Darker', color: '#000000', field: 'primaryDarker' },
    { direction: -1, factor: 5, name: 'Primary Dark', color: '#000000', field: 'primaryDark' },
    { direction: 1, factor: 0, name: 'Primary', color: '#000000', field: 'primary' },
    { direction: 1, factor: 30, name: 'Primary Light', color: '#000000', field: 'primaryLight' },
    { direction: 1, factor: 70, name: 'Primary Lighter', color: '#000000', field: 'primaryLighter' },
    { direction: -1, factor: 10, name: 'Secondary Darker', color: '#5F3A79', field: 'secondaryDarker' },
    { direction: -1, factor: 5, name: 'Secondary Dark', color: '#5F3A79', field: 'secondaryDark' },
    { direction: 1, factor: 0, name: 'Secondary', color: '#5F3A79', field: 'secondary' },
    { direction: 1, factor: 40, name: 'Secondary Light', color: '#5F3A79', field: 'secondaryLight' },
    { direction: 1, factor: 45, name: 'Secondary Lighter', color: '#5F3A79', field: 'secondaryLighter' },
    { direction: -1, factor: 10, name: 'Tertiary Darker', color: '#98AC4D', field: 'tertiaryDarker' },
    { direction: -1, factor: 5, name: 'Tertiary Dark', color: '#98AC4D', field: 'tertiaryDark' },
    { direction: 1, factor: 0, name: 'Tertiary', color: '#98AC4D', field: 'tertiary' },
    { direction: 1, factor: 40, name: 'Tertiary Light', color: '#98AC4D', field: 'tertiaryLight' },
    { direction: 1, factor: 45, name: 'Tertiary Lighter', color: '#98AC4D', field: 'tertiaryLighter' },
    { direction: 1, factor: 0, name: 'Accent', color: '#1E1A76', field: 'accent' },
];

/**
 * Was ein Kunde im Firmenprofil selbst wählt: zwei Farben, nicht sechzehn.
 *
 * `styleCalculator` oben beschreibt die ganze Familie, wie die Oberfläche sie braucht — Abstufungen
 * von Primär und Sekundär, dazu Tertiär und Akzent. Gepflegt wird davon nur, was hier steht: Die
 * Abstufungen werden aus der gewählten Farbe gerechnet (`themeTokens`), und Tertiär wie Akzent hat
 * `CustomerTheme` nie gespeichert — das Profil zeigte sechzehn Felder, von denen die meisten beim
 * Speichern verworfen wurden.
 */
export const EDITABLE_THEME_COLORS: Array<{ field: 'primary' | 'secondary'; labelKey: string }> = [
    { field: 'primary', labelKey: 'settingsPanel.companyPanel.colorPrimary' },
    { field: 'secondary', labelKey: 'settingsPanel.companyPanel.colorSecondary' },
];

const calculateThemeColor = (parameters: any) => {
    if (parameters.direction > 0) {
        return tinycolor(parameters.color).lighten(parameters.factor).toHexString();
    } else {
        return tinycolor(parameters.color).darken(parameters.factor).toHexString();
    }
}

// Die Primärfamilie kommt aus der Marke des Hosts (siehe src/brand/brand.ts).
// Für The Real Insight ist das unverändert Schwarz, für FireIQ das Grün der
// FireIQ-App; die übrigen Farben bleiben für beide Marken gleich.
const brandStyleCalculator = styleCalculator.map((parameters: any) =>
    parameters.field.startsWith('primary')
        ? { ...parameters, color: BRAND.accentColor }
        : parameters,
);

export const defaultTheme = brandStyleCalculator.reduce((map: any, parameters: any) => {
    map[parameters.field] = calculateThemeColor(parameters);

    return map;
}, {});

const calculatePartialTheme = (theme: any) => {
    return {
        palette: {
            themePrimary: theme.primary,
            themeDark: theme.primaryDark,
            themeDarker: theme.primaryDarker,
            themeLight: theme.primaryLight,
            themeLighter: theme.primaryLighter,
            white: '#FFFFFF',
            black: '#000000',
            neutralLighter: '#F3F2F1',
            neutralLight: '#EDEBE9',
            neutral: '#E1DFDD',
            neutralDark: '#C8C6C4',
            neutralDarker: '#8A8886',
            neutralDarkest: '#323130',
        },
    }
}

// Kundenfarben zusätzlich als CSS-Variablen (--customer-primary-dark …)
// bereitstellen, damit shadcn-basierte Komponenten sie ohne Inline-Styles
// über Tailwind-Arbitrary-Werte nutzen können. Rein additiv: die bisherigen
// Variablen und der ThemeProvider-Pfad bleiben unverändert.
const CUSTOMER_CSS_FIELDS = styleCalculator.map((parameters: any) => parameters.field);

const toKebabCase = (field: string) =>
    field.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);

const adjustCSSColors = (theme: any) => {
    console.log('🎨 adjustCSSColors called with theme:', theme);
    console.log('🎨 Theme keys:', Object.keys(theme));
    document.body.style.setProperty('--primary-color', theme.primary);

    const merged = { ...defaultTheme, ...theme };
    CUSTOMER_CSS_FIELDS.forEach((field: string) => {
        if (merged[field]) {
            document.body.style.setProperty(`--customer-${toKebabCase(field)}`, merged[field]);
        }
    });

    // Und dieselbe Farbe noch einmal für shadcn.
    //
    // Dessen Komponenten lesen `--primary` und `--ring` als HSL-Tripel und kennen die Variablen
    // oben nicht. Solange nur die oben gesetzt wurden, zeigten sie ihren eingebauten Wert — ein
    // dunkles Schiefergrau-Blau — gleich welcher Kunde angemeldet ist. Das ist kein fehlendes Blau,
    // sondern das falsche, und es betrifft mehr Dateien als das ältere System erreicht.
    Object.entries(shadcnTokensFor(merged.primary)).forEach(([name, value]) => {
        if (value) document.body.style.setProperty(name, value);
    });
    
    // Use secondary from theme, with fallback to hardcoded value
    const secondaryLight = theme.secondary || '#AD2389';
    console.log('🎨 Setting --theme-secondary-light to:', secondaryLight);
    document.body.style.setProperty('--theme-secondary-light', secondaryLight);
    
    // Use primary light from theme, with fallback to hardcoded value
    const primaryLight = theme.primaryLight || '#CCCCCC';
    console.log('🎨 Setting --theme-primary-light to:', primaryLight);
    document.body.style.setProperty('--theme-primary-light', primaryLight);
}

interface Properties {
    children?: any;
}

export function CustomerThemeProvider(properties: Properties) {
    const [searchParams] = useSearchParams();
    const [, setTheme] = useAtom(themeAtom);
    const [, setThemeLoading] = useAtom(themeLoadingAtom);
    const [authentication] = useAtom(authenticationAtom);
    const [partialTheme, setPartialTheme] = useState(calculatePartialTheme(defaultTheme));
    // Only these two inputs decide the theme. Depending on the whole
    // searchParams object would re-run the fetch on every unrelated URL change
    // (e.g. ?conversationId= while switching chats).
    const tenantFromUrl = searchParams.get('tenant');
    const customer = authentication?.customer;

    useEffect(() => {
        const fetchTheme = async () => {
            setThemeLoading(true);
            try {
                // Priority 0: a branded host (FireIQ) is pinned. Neither the
                // ?tenant= param nor the signed-in customer may repaint it, so
                // the portal stays that brand for every user — the same
                // decision the FireIQ app takes for its build variant.
                if (BRAND.defaultTenant) {
                    setTheme(defaultTheme);
                    setPartialTheme(calculatePartialTheme(defaultTheme));
                    adjustCSSColors(defaultTheme);
                }
                // Priority 1: Check for tenant URL parameter
                else if (!!tenantFromUrl) {
                    if (tenantFromUrl === 'null') {
                        setTheme(defaultTheme);
                        setPartialTheme(calculatePartialTheme(defaultTheme));
                        adjustCSSColors(defaultTheme);
                    } else {
                        // Einmal richten, dann überall dieselbe Farbe: die gespeicherten
                        // Abstufungen sind bei jedem Mandanten schwarz und weiß geblieben.
                        const theme = normalizeTheme(await userAPI.getTheme(tenantFromUrl));

                        console.log('Customer theme retrieved from URL tenant >>>', theme);

                        setTheme(theme);
                        setPartialTheme(calculatePartialTheme(theme));
                        adjustCSSColors(theme);
                    }
                } 
                // Priority 2: Check for authenticated customer
                else if (customer) {
                    try {
                        console.log('🎨 Attempting to fetch theme for customer:', customer);

                        const theme = normalizeTheme(await userAPI.getTheme(customer));

                        console.log('✅ Customer theme retrieved from API:', {
                            hasLogoURL: !!theme.logoURL,
                            logoURL: theme.logoURL,
                            themeKeys: Object.keys(theme)
                        });

                        setTheme(theme);
                        setPartialTheme(calculatePartialTheme(theme));
                        adjustCSSColors(theme);
                    } catch (e) {
                        console.error('🚨 THEME API FAILED for customer', customer, e);
                        console.log('📋 This needs to be fixed on the server side!');
                        
                        // Fallback to default theme if customer theme fails
                        setTheme(defaultTheme);
                        setPartialTheme(calculatePartialTheme(defaultTheme));
                        adjustCSSColors(defaultTheme);
                    }
                }
                // Priority 3: Use default theme
                else {
                    setTheme(defaultTheme);
                    setPartialTheme(calculatePartialTheme(defaultTheme));
                    adjustCSSColors(defaultTheme);
                }
            } catch (e) {
                console.log('Failed to obtain theme', e);
                // Fallback to default theme on any error
                setTheme(defaultTheme);
                setPartialTheme(calculatePartialTheme(defaultTheme));
                adjustCSSColors(defaultTheme);
            } finally {
                setThemeLoading(false);
            }
        }

        fetchTheme();
    }, [tenantFromUrl, customer, setTheme, setThemeLoading]);

    return <div>
        <ThemeProvider theme={partialTheme}>
            {properties && properties.children
                ?
                (properties.children)
                :
                <></>
            }
        </ThemeProvider>
    </div>;
}

export const useCustomerTheme = () => {
    const [theme, setTheme] = useAtom(themeAtom) as any;
    const [themeLoading] = useAtom(themeLoadingAtom);

    // Always merge theme with defaultTheme to preserve logoURL even if no custom colors
    const finalTheme = {...defaultTheme, ...theme};

    return {
        theme: finalTheme,
        themeLoading,
        setTheme,
    }
}

